import { useMemo, useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { MDA_OPTIONS, UPDATE_FREQUENCY_LABELS, mdaName } from '../../lib/constants';
import type { Dataset, DatasetField, Page, UpdateFrequency } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

function stewardMdasOf(roles: string[]): string[] {
  return roles.filter((r) => r.startsWith('DATA_STEWARD:')).map((r) => r.split(':')[1]!);
}

export function StaffDatasets() {
  const { persona } = useAuth();
  const stewardMdas = stewardMdasOf(persona?.roles ?? []);
  const [showDataset, setShowDataset] = useState(false);
  const [showBudgetLine, setShowBudgetLine] = useState(false);

  const datasetsQ = useApiQuery<Page<Dataset>>('/api/v1/opengov/datasets?limit=100');

  return (
    <div>
      <PageHeader
        title="Datasets & budget data"
        subtitle="Publish open datasets and ingest budget-line rows for the ministries/departments you steward."
        actions={
          stewardMdas.length > 0 ? (
            <>
              <button className="btn-secondary" onClick={() => setShowBudgetLine(true)}>
                + Ingest budget line
              </button>
              <button className="btn-primary" onClick={() => setShowDataset(true)}>
                + Publish dataset
              </button>
            </>
          ) : undefined
        }
      />

      {stewardMdas.length === 0 ? (
        <div className="mb-4">
          <ErrorNote message="Your account does not hold a DATA_STEWARD:<mda> role — you can browse the catalogue below, but publishing actions will be rejected by the API." />
        </div>
      ) : (
        <p className="mb-4 text-xs text-slate-500">You steward: {stewardMdas.map(mdaName).join(', ')}.</p>
      )}

      {datasetsQ.error ? <ErrorNote message={datasetsQ.error} /> : null}
      <div className="card overflow-x-auto">
        {datasetsQ.loading ? (
          <Spinner />
        ) : (datasetsQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No datasets published yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Code</th>
                <th className="table-head">Title</th>
                <th className="table-head">Owner MDA</th>
                <th className="table-head">Frequency</th>
                <th className="table-head">API</th>
              </tr>
            </thead>
            <tbody>
              {datasetsQ.data!.items.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs font-semibold">{d.code}</td>
                  <td className="table-cell font-medium">{d.title}</td>
                  <td className="table-cell">{d.ownerMda}</td>
                  <td className="table-cell">
                    <StatusBadge status={d.updateFrequency} />
                  </td>
                  <td className="table-cell">{d.apiEnabled ? 'Live' : 'Disabled'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showDataset ? (
        <PublishDatasetModal
          mdas={stewardMdas}
          onClose={() => setShowDataset(false)}
          onDone={() => {
            setShowDataset(false);
            datasetsQ.reload();
          }}
        />
      ) : null}
      {showBudgetLine ? <IngestBudgetLineModal mdas={stewardMdas} onClose={() => setShowBudgetLine(false)} /> : null}
    </div>
  );
}

function PublishDatasetModal({ mdas, onClose, onDone }: { mdas: string[]; onClose: () => void; onDone: () => void }) {
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [ownerMda, setOwnerMda] = useState(mdas[0] ?? '');
  const [updateFrequency, setUpdateFrequency] = useState<UpdateFrequency>('QUARTERLY');
  const [apiEnabled, setApiEnabled] = useState(true);
  const [sourceTable, setSourceTable] = useState<'BUDGET_LINES' | 'PUBLIC_NOTICES'>('BUDGET_LINES');
  const [fields, setFields] = useState<DatasetField[]>([{ name: '', type: 'string', description: '' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validFields = useMemo(() => fields.filter((f) => f.name.trim() && f.type.trim()), [fields]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/opengov/datasets', {
        code: code.trim().toUpperCase(),
        title: title.trim(),
        description: description.trim() || undefined,
        ownerMda,
        schema: { fields: validFields },
        updateFrequency,
        apiEnabled,
        sourceTable,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to publish dataset');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Publish dataset" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Dataset code (upper-kebab)">
            <input
              className="field-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g. MOH-BUDGET-EXECUTION"
            />
          </Field>
          <Field label="Owner MDA">
            <select className="field-input" value={ownerMda} onChange={(e) => setOwnerMda(e.target.value)}>
              {mdas.map((code) => (
                <option key={code} value={code}>
                  {mdaName(code)}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Update frequency">
            <select
              className="field-input"
              value={updateFrequency}
              onChange={(e) => setUpdateFrequency(e.target.value as UpdateFrequency)}
            >
              {Object.entries(UPDATE_FREQUENCY_LABELS).map(([code, label]) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Backing table">
            <select
              className="field-input"
              value={sourceTable}
              onChange={(e) => setSourceTable(e.target.value as 'BUDGET_LINES' | 'PUBLIC_NOTICES')}
            >
              <option value="BUDGET_LINES">Budget lines</option>
              <option value="PUBLIC_NOTICES">Public notices</option>
            </select>
          </Field>
          <label className="mt-6 flex items-center gap-2 text-sm font-medium text-slate-700">
            <input type="checkbox" checked={apiEnabled} onChange={(e) => setApiEnabled(e.target.checked)} />
            Live JSON/CSV export enabled
          </label>
        </div>

        <div>
          <span className="field-label">Field schema</span>
          <div className="space-y-2">
            {fields.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  className="field-input"
                  placeholder="field name"
                  value={f.name}
                  onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                />
                <input
                  className="field-input max-w-[140px]"
                  placeholder="type"
                  value={f.type}
                  onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, type: e.target.value } : x)))}
                />
                <input
                  className="field-input"
                  placeholder="description (optional)"
                  value={f.description ?? ''}
                  onChange={(e) => setFields(fields.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))}
                />
                <button
                  type="button"
                  className="rounded-md px-2 py-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                  onClick={() => setFields(fields.filter((_, j) => j !== i))}
                  aria-label="Remove field"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="btn-secondary mt-2 !px-2.5 !py-1 text-xs"
            onClick={() => setFields([...fields, { name: '', type: 'string', description: '' }])}
          >
            + Add field
          </button>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !code.trim() || !title.trim() || !ownerMda}>
            {busy ? 'Publishing…' : 'Publish dataset'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function IngestBudgetLineModal({ mdas, onClose }: { mdas: string[]; onClose: () => void }) {
  const [form, setForm] = useState({
    year: String(new Date().getFullYear()),
    mda: mdas[0] ?? '',
    programme: '',
    allocated: '',
    revised: '',
    spent: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/opengov/budget-lines/ingest', {
        rows: [
          {
            year: Number.parseInt(form.year, 10),
            mda: form.mda,
            programme: form.programme.trim(),
            allocated: Number.parseFloat(form.allocated),
            revised: Number.parseFloat(form.revised || form.allocated),
            spent: Number.parseFloat(form.spent || '0'),
          },
        ],
      });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to ingest budget line');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Ingest budget line" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {done ? (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            Budget line ingested — visible immediately on the public Budget Explorer.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fiscal year">
                <input
                  className="field-input"
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                />
              </Field>
              <Field label="Ministry / department">
                <select className="field-input" value={form.mda} onChange={(e) => setForm({ ...form, mda: e.target.value })}>
                  {mdas.map((code) => (
                    <option key={code} value={code}>
                      {mdaName(code)}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <Field label="Programme">
              <input
                className="field-input"
                value={form.programme}
                onChange={(e) => setForm({ ...form, programme: e.target.value })}
                placeholder="e.g. Primary Healthcare Services"
              />
            </Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Allocated (SCR)">
                <input
                  className="field-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.allocated}
                  onChange={(e) => setForm({ ...form, allocated: e.target.value })}
                />
              </Field>
              <Field label="Revised (SCR)">
                <input
                  className="field-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.revised}
                  onChange={(e) => setForm({ ...form, revised: e.target.value })}
                  placeholder={form.allocated || '0.00'}
                />
              </Field>
              <Field label="Spent (SCR)">
                <input
                  className="field-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.spent}
                  onChange={(e) => setForm({ ...form, spent: e.target.value })}
                  placeholder="0.00"
                />
              </Field>
            </div>
            {form.allocated ? (
              <p className="text-xs text-slate-400">Allocated preview: {scr(Number.parseFloat(form.allocated) || 0)}</p>
            ) : null}
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done ? (
            <button
              className="btn-primary"
              onClick={submit}
              disabled={busy || !form.mda || !form.programme.trim() || !form.allocated}
            >
              {busy ? 'Ingesting…' : 'Ingest row'}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
